import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export type Language = 'TR' | 'EN';

// Define all possible translation keys
export type TranslationKey = 
  | 'header.menu' | 'header.search' | 'header.login' | 'header.profile' | 'header.orders' | 'header.logout'
  | 'header.search.placeholder' | 'header.search.hint' | 'header.search.minimum' | 'header.search.searching'
  | 'header.search.noResults' | 'header.search.viewAll'
  | 'nav.collection' | 'nav.blog' | 'nav.about' | 'nav.sustainability' | 'nav.contact' | 'nav.stores'
  | 'product.collection' | 'product.addToBag' | 'product.color' | 'product.details' | 'product.shipping'
  | 'product.findInStore' | 'product.related' | 'product.similar'
  | 'cart.title' | 'cart.subtotal' | 'cart.total' | 'cart.checkout' | 'cart.empty' | 'cart.goToCart' | 'cart.added'
  | 'drawer.shipping.title' | 'drawer.shipping.intro' | 'drawer.shipping.section' | 'drawer.shipping.free'
  | 'drawer.shipping.time' | 'drawer.shipping.packaging' | 'drawer.shipping.tracking' | 'drawer.shipping.returns'
  | 'drawer.shipping.returnDays' | 'drawer.shipping.originalPackaging' | 'drawer.shipping.unused' | 'drawer.shipping.freeReturn'
  | 'drawer.details.title' | 'drawer.details.features' | 'drawer.details.madeInItaly' | 'drawer.details.leather'
  | 'drawer.details.pointedToe' | 'drawer.details.stiletto' | 'drawer.details.height'
  | 'page.blog.title' | 'page.blog.subtitle' | 'page.blog.craftsmanship' | 'page.blog.sustainability'
  | 'language.title' | 'language.turkish' | 'language.english'
  | 'login.title' | 'login.email' | 'login.password' | 'login.emailPlaceholder' | 'login.passwordPlaceholder'
  | 'login.submit' | 'login.noAccount' | 'login.register' | 'login.welcome' | 'login.profile' | 'login.orders' | 'login.logout'
  | 'breadcrumb.home' | 'breadcrumb.women' | 'breadcrumb.shoes' | 'breadcrumb.pumps';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations = {
  TR: {
    // Header
    'header.menu': 'Menu',
    'header.search': 'Ara',
    'header.login': 'Giriş Yap',
    'header.profile': 'Hesabım',
    'header.orders': 'Siparişlerim',
    'header.logout': 'Çıkış',
    'header.search.placeholder': 'Ürün ara...',
    'header.search.hint': 'Koleksiyonumuzda arama yapmak için yazmaya başlayın',
    'header.search.minimum': 'Aramak için en az 2 karakter yazın',
    'header.search.searching': 'Aranıyor...',
    'header.search.noResults': '"{query}" için ürün bulunamadı',
    'header.search.viewAll': 'Tüm Sonuçları Görüntüle',

    // Navigation
    'nav.collection': 'Koleksiyon',
    'nav.blog': 'Blog',
    'nav.about': 'Hikayemiz',
    'nav.sustainability': 'Sürdürülebilirlik',
    'nav.contact': 'İletişim',
    'nav.stores': 'Mağazalar',

    // Product Detail
    'product.collection': 'KOLEKSİYON 2025',
    'product.addToBag': 'Sepete Ekle',
    'product.color': 'Renk',
    'product.details': 'Ürün Detayları',
    'product.shipping': 'Ücretsiz Gönderim ve İade',
    'product.findInStore': 'Mağazada Bul',
    'product.related': 'Bunları da beğenebilirsiniz',
    'product.similar': 'Benzer Ürünler',

    // Cart
    'cart.title': 'Alışveriş sepeti',
    'cart.subtotal': 'Ara toplam',
    'cart.total': 'Toplam',
    'cart.checkout': 'Ödeme Yap',
    'cart.empty': 'Sepetiniz boş',
    'cart.goToCart': 'Alışveriş sepetine git',
    'cart.added': 'Ürün alışveriş sepetine eklendi',

    // Drawers
    'drawer.shipping.title': 'Ücretsiz Gönderim ve İade',
    'drawer.shipping.intro': 'Tüm siparişlerde ücretsiz gönderim. Ürünler 1-2 iş günü içinde kargoya verilir.',
    'drawer.shipping.section': 'Gönderim',
    'drawer.shipping.free': 'Ücretsiz standart gönderim',
    'drawer.shipping.time': '1-2 iş günü içinde kargoya verilir',
    'drawer.shipping.packaging': 'Güvenli paketleme',
    'drawer.shipping.tracking': 'Takip numarası ile izleme',
    'drawer.shipping.returns': 'İade',
    'drawer.shipping.returnDays': '14 gün içinde iade',
    'drawer.shipping.originalPackaging': 'Orijinal ambalaj gerekli',
    'drawer.shipping.unused': 'Kullanılmamış ürünler',
    'drawer.shipping.freeReturn': 'Ücretsiz iade gönderimi',

    'drawer.details.title': 'Ürün Detayları',
    'drawer.details.features': 'Özellikler',
    'drawer.details.madeInItaly': 'İtalya\'da üretilmiştir',
    'drawer.details.leather': 'Deri üst ve taban',
    'drawer.details.pointedToe': 'Sivri burun',
    'drawer.details.stiletto': 'Stiletto topuk',
    'drawer.details.height': 'Yükseklik: 85mm',

    // Pages
    'page.blog.title': 'Blog',
    'page.blog.subtitle': 'Zanaatkarlık ve Zarafet',
    'page.blog.craftsmanship': 'Zanaatkarlık',
    'page.blog.sustainability': 'Sürdürülebilirlik',

    // Language Selection
    'language.title': 'Dil Seçimi',
    'language.turkish': 'Türkçe',
    'language.english': 'English',

    // Login
    'login.title': 'Giriş Yap',
    'login.email': 'E-posta',
    'login.password': 'Şifre',
    'login.emailPlaceholder': 'E-posta adresiniz',
    'login.passwordPlaceholder': 'Şifreniz',
    'login.submit': 'Giriş Yap',
    'login.noAccount': 'Henüz hesabınız yok mu?',
    'login.register': 'Üye Ol',
    'login.welcome': 'Hoş geldiniz, {name}',
    'login.profile': 'Profilim',
    'login.orders': 'Siparişlerim',
    'login.logout': 'Çıkış Yap',

    // Breadcrumbs
    'breadcrumb.home': 'Home',
    'breadcrumb.women': 'Women',
    'breadcrumb.shoes': 'Shoes',
    'breadcrumb.pumps': 'Pumps & Ballet',
  },
  EN: {
    // Header
    'header.menu': 'Menu',
    'header.search': 'Search',
    'header.login': 'Login',
    'header.profile': 'My Account',
    'header.orders': 'My Orders',
    'header.logout': 'Logout',
    'header.search.placeholder': 'Search products...',
    'header.search.hint': 'Start typing to search our collection',
    'header.search.minimum': 'Enter at least 2 characters to search',
    'header.search.searching': 'Searching...',
    'header.search.noResults': 'No products found for "{query}"',
    'header.search.viewAll': 'View All Results',

    // Navigation
    'nav.collection': 'Collection',
    'nav.blog': 'Blog',
    'nav.about': 'Our Story',
    'nav.sustainability': 'Sustainability',
    'nav.contact': 'Contact',
    'nav.stores': 'Stores',

    // Product Detail
    'product.collection': 'COLLECTION 2025',
    'product.addToBag': 'Add to Bag',
    'product.color': 'Color',
    'product.details': 'Product Details',
    'product.shipping': 'Free Shipping & Returns',
    'product.findInStore': 'Find in Store',
    'product.related': 'You might also like',
    'product.similar': 'Similar Products',

    // Cart
    'cart.title': 'Shopping Cart',
    'cart.subtotal': 'Subtotal',
    'cart.total': 'Total',
    'cart.checkout': 'Checkout',
    'cart.empty': 'Your cart is empty',
    'cart.goToCart': 'Go to Shopping Cart',
    'cart.added': 'Product added to shopping cart',

    // Drawers
    'drawer.shipping.title': 'Free Shipping & Returns',
    'drawer.shipping.intro': 'Free shipping on all orders. Products are shipped within 1-2 business days.',
    'drawer.shipping.section': 'Shipping',
    'drawer.shipping.free': 'Free standard shipping',
    'drawer.shipping.time': 'Shipped within 1-2 business days',
    'drawer.shipping.packaging': 'Secure packaging',
    'drawer.shipping.tracking': 'Tracking with tracking number',
    'drawer.shipping.returns': 'Returns',
    'drawer.shipping.returnDays': 'Returns within 14 days',
    'drawer.shipping.originalPackaging': 'Original packaging required',
    'drawer.shipping.unused': 'Unused products',
    'drawer.shipping.freeReturn': 'Free return shipping',

    'drawer.details.title': 'Product Details',
    'drawer.details.features': 'Features',
    'drawer.details.madeInItaly': 'Made in Italy',
    'drawer.details.leather': 'Leather upper and sole',
    'drawer.details.pointedToe': 'Pointed toe',
    'drawer.details.stiletto': 'Stiletto heel',
    'drawer.details.height': 'Height: 85mm',

    // Pages
    'page.blog.title': 'Blog',
    'page.blog.subtitle': 'Craftsmanship & Elegance',
    'page.blog.craftsmanship': 'Craftsmanship',
    'page.blog.sustainability': 'Sustainability',

    // Language Selection
    'language.title': 'Language Selection',
    'language.turkish': 'Türkçe',
    'language.english': 'English',

    // Login
    'login.title': 'Login',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.emailPlaceholder': 'Your email address',
    'login.passwordPlaceholder': 'Your password',
    'login.submit': 'Login',
    'login.noAccount': 'Don\'t have an account yet?',
    'login.register': 'Register',
    'login.welcome': 'Welcome, {name}',
    'login.profile': 'My Profile',
    'login.orders': 'My Orders',
    'login.logout': 'Logout',

    // Breadcrumbs
    'breadcrumb.home': 'Home',
    'breadcrumb.women': 'Women',
    'breadcrumb.shoes': 'Shoes',
    'breadcrumb.pumps': 'Pumps & Ballet',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('TR');

  useEffect(() => {
    // Load language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'TR' || savedLanguage === 'EN')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang === 'TR' ? 'tr' : 'en';
  }, []);

  // Set initial html lang attribute
  useEffect(() => {
    document.documentElement.lang = language === 'TR' ? 'tr' : 'en';
  }, [language]);

  const t = useCallback((key: TranslationKey, params?: Record<string, string>): string => {
    const currentTranslations = translations[language];
    let result = currentTranslations[key] || key;
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
      });
    }
    return result;
  }, [language]);

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t
  }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};


